import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Activity, Thermometer, Move, AlertTriangle, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import logoHorizontal from "@/assets/logo-horizontal.png";
import StressQuestionnaire from "@/components/StressQuestionnaire";

interface SensorReading {
  id: string;
  recorded_at: string;
  heart_rate: number;
  hrv: number;
  skin_temp: number;
  accel_x: number;
  accel_y: number;
  accel_z: number;
  is_stress_peak: boolean;
}

interface StressEvent {
  id: string;
  detected_at: string;
  reading_id: string;
  status: string;
}

const Dashboard = () => {
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [stressEvents, setStressEvents] = useState<StressEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<StressEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userName, setUserName] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate("/auth"); return; }

    const { data: profile } = await supabase
      .from("profiles")
      .select("name")
      .eq("user_id", session.user.id)
      .single();
    setUserName(profile?.name || session.user.email || "");

    const { data: readingsData } = await supabase
      .from("sensor_readings")
      .select("*")
      .eq("user_id", session.user.id)
      .order("recorded_at", { ascending: true });

    if (!readingsData || readingsData.length === 0) {
      // Generate mock data
      setGenerating(true);
      try {
        const resp = await supabase.functions.invoke("generate-sensor-data", {
          body: { userId: session.user.id },
        });
        if (resp.error) throw resp.error;
        // Re-fetch after generation
        const { data: fresh } = await supabase
          .from("sensor_readings")
          .select("*")
          .eq("user_id", session.user.id)
          .order("recorded_at", { ascending: true });
        setReadings((fresh as SensorReading[]) || []);
      } catch (err: any) {
        toast({ title: "Erro ao gerar dados", description: err.message, variant: "destructive" });
      }
      setGenerating(false);
    } else {
      setReadings(readingsData as SensorReading[]);
    }

    const { data: events } = await supabase
      .from("stress_events")
      .select("*")
      .eq("user_id", session.user.id)
      .order("detected_at", { ascending: true });
    setStressEvents((events as StressEvent[]) || []);
    setLoading(false);
  }, [navigate, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const chartData = readings.map(r => ({
    time: new Date(r.recorded_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    heartRate: Number(r.heart_rate),
    hrv: Number(r.hrv),
    skinTemp: Number(r.skin_temp),
    movement: Math.sqrt(Number(r.accel_x) ** 2 + Number(r.accel_y) ** 2 + Number(r.accel_z) ** 2).toFixed(1),
    isStress: r.is_stress_peak,
    id: r.id,
  }));

  const stressPoints = chartData.filter(d => d.isStress);

  const handleStressClick = (readingId: string) => {
    const event = stressEvents.find(e => e.reading_id === readingId);
    if (event) setSelectedEvent(event);
  };

  if (loading || generating) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
        <p className="text-muted-foreground">
          {generating ? "Gerando dados dos sensores..." : "Carregando..."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jade-light font-body">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoHorizontal} alt="MentalSync" className="h-8" />
            <span className="text-sm font-medium text-muted-foreground hidden sm:inline">Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground font-medium hidden sm:inline">Olá, {userName}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        <h1 className="font-display text-2xl font-bold text-foreground">
          Monitoramento de Saúde Mental
        </h1>

        {/* Stress alert */}
        {stressEvents.filter(e => e.status === "pending").length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
            <div>
              <p className="font-semibold text-foreground">Região de estresse detectada!</p>
              <p className="text-sm text-muted-foreground">
                Clique nos pontos vermelhos nos gráficos para responder o questionário.
              </p>
            </div>
          </div>
        )}

        {/* Heart Rate Chart */}
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-gold" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Frequência Cardíaca / HRV <span className="text-xs text-muted-foreground font-body">(MAX30102)</span>
            </h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(120 10% 88%)" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <Tooltip />
                <Line type="monotone" dataKey="heartRate" name="BPM" stroke="hsl(0 70% 55%)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="hrv" name="HRV (ms)" stroke="hsl(200 70% 50%)" strokeWidth={2} dot={false} />
                {stressPoints.map((p, i) => (
                  <ReferenceDot
                    key={i}
                    x={p.time}
                    y={p.heartRate}
                    r={8}
                    fill="hsl(0 84% 60%)"
                    stroke="white"
                    strokeWidth={2}
                    onClick={() => handleStressClick(p.id)}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Skin Temperature Chart */}
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Thermometer className="h-5 w-5 text-gold" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Temperatura da Pele <span className="text-xs text-muted-foreground font-body">(NTC 10K)</span>
            </h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(120 10% 88%)" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <YAxis domain={[30, 38]} tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <Tooltip />
                <Line type="monotone" dataKey="skinTemp" name="°C" stroke="hsl(30 80% 55%)" strokeWidth={2} dot={false} />
                {stressPoints.map((p, i) => (
                  <ReferenceDot
                    key={i}
                    x={p.time}
                    y={chartData.find(d => d.time === p.time)?.skinTemp || 0}
                    r={8}
                    fill="hsl(0 84% 60%)"
                    stroke="white"
                    strokeWidth={2}
                    onClick={() => handleStressClick(p.id)}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Movement Chart */}
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Move className="h-5 w-5 text-gold" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Movimento Corporal <span className="text-xs text-muted-foreground font-body">(MPU6050)</span>
            </h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(120 10% 88%)" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(150 5% 45%)" />
                <Tooltip />
                <Line type="monotone" dataKey="movement" name="Magnitude" stroke="hsl(270 50% 55%)" strokeWidth={2} dot={false} />
                {stressPoints.map((p, i) => (
                  <ReferenceDot
                    key={i}
                    x={p.time}
                    y={Number(chartData.find(d => d.time === p.time)?.movement || 0)}
                    r={8}
                    fill="hsl(0 84% 60%)"
                    stroke="white"
                    strokeWidth={2}
                    onClick={() => handleStressClick(p.id)}
                    style={{ cursor: "pointer" }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>

      {/* Questionnaire modal */}
      {selectedEvent && (
        <StressQuestionnaire
          stressEvent={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onComplete={() => {
            setSelectedEvent(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
